import React from 'react';
import { FileText, AlertTriangle, Scale, Shield, Building, DollarSign } from 'lucide-react';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-navy-100 rounded-full mb-6">
            <Scale className="h-4 w-4 text-navy-600 mr-2" />
            <span className="text-navy-700 text-sm font-semibold">Terms of Service</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-4">
            Terms of Service
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Legal terms and conditions for using HULA CAPITAL LIMITED services
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Last updated: December 2024
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="prose prose-lg max-w-none">
            
            {/* Important Notice */}
            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8">
              <div className="flex items-center mb-2">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-red-800">Important Notice</h3>
              </div>
              <p className="text-red-700 text-sm">
                These terms contain important information about your legal rights, remedies, and obligations. Please read them carefully. 
                By accessing or using our services, you agree to be bound by these terms.
              </p>
            </div>

            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4 flex items-center">
                <Building className="h-6 w-6 text-gold-600 mr-2" />
                1. Introduction and Company Information
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                These Terms of Service ("Terms") govern your relationship with HULA CAPITAL LIMITED ("Company," "we," "our," or "us") 
                when you access our website or use our financial services.
              </p>
              <div className="bg-gold-50 p-4 rounded-lg border-l-4 border-gold-500 mb-4">
                <p className="text-sm text-gray-700">
                  <strong>Company Details:</strong><br />
                  HULA CAPITAL LIMITED<br />
                  Company Registration: 16534250 (England & Wales)<br />
                  Registered Office: 10th Floor 19 Sun St, London, EC2A 2FJ, United Kingdom<br />
                  Business Classification: Financial management activities (SIC: 70221)
                </p>
              </div>
              <p className="text-gray-600">
                By accessing our website or using our services, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms and our Privacy Policy.
              </p>
            </section>

            {/* Services Description */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4 flex items-center">
                <DollarSign className="h-6 w-6 text-gold-600 mr-2" />
                2. Our Services
              </h2>
              <p className="text-gray-600 mb-4">HULA CAPITAL LIMITED provides the following financial services:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Investment management and advisory services</li>
                <li>Portfolio management across various asset classes</li>
                <li>Financial planning and wealth management</li>
                <li>Investment research and market analysis</li>
                <li>Risk assessment and management</li>
                <li>Loan and credit facilities</li>
                <li>Digital financial platforms and tools</li>
              </ul>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Important:</strong> All investment services are subject to market risks. Past performance 
                  does not guarantee future results. You may lose some or all of your investment.
                </p>
              </div>
            </section>

            {/* Eligibility */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">3. Eligibility and Account Requirements</h2>
              
              <h3 className="text-xl font-semibold text-navy-800 mb-3">Eligibility Criteria</h3>
              <p className="text-gray-600 mb-4">To use our services, you must:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Be at least 18 years of age</li>
                <li>Have legal capacity to enter into binding contracts</li>
                <li>Provide accurate and complete information</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Meet minimum investment requirements where applicable</li>
                <li>Successfully complete our verification and KYC procedures</li>
              </ul>

              <h3 className="text-xl font-semibold text-navy-800 mb-3">Account Registration</h3>
              <p className="text-gray-600 mb-4">When creating an account, you agree to:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Provide truthful, accurate, and complete information</li>
                <li>Maintain the confidentiality of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Update your information as necessary to keep it current</li>
              </ul>
            </section>

            {/* Investment Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">4. Investment Terms and Conditions</h2>
              
              <h3 className="text-xl font-semibold text-navy-800 mb-3">Investment Process</h3>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>All investments are subject to our risk assessment procedures</li>
                <li>Minimum investment amounts may apply to certain products</li>
                <li>Investment instructions must be submitted through authorized channels</li>
                <li>We reserve the right to reject investment instructions at our discretion</li>
                <li>Settlement periods vary by asset class and market conditions</li>
              </ul>

              <h3 className="text-xl font-semibold text-navy-800 mb-3">Risk Disclosure</h3>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
                <p className="text-yellow-800 text-sm font-medium">
                  <strong>Investment Risks:</strong> All investments carry risk of loss. You should carefully 
                  consider your financial situation, investment objectives, and risk tolerance before investing.
                </p>
              </div>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Market risk: Value of investments may fluctuate</li>
                <li>Liquidity risk: Some investments may be difficult to sell quickly</li>
                <li>Credit risk: Issuers may default on obligations</li>
                <li>Currency risk: Foreign exchange fluctuations may affect returns</li>
                <li>Inflation risk: Rising prices may erode purchasing power</li>
              </ul>
            </section>

            {/* Fees and Charges */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">5. Fees and Charges</h2>
              <p className="text-gray-600 mb-4">Our fee structure includes:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Management fees based on assets under management</li>
                <li>Performance fees where applicable</li>
                <li>Transaction and trading fees</li>
                <li>Administrative and custody fees</li>
                <li>Third-party costs and charges</li>
              </ul>
              <p className="text-gray-600 mb-4">
                Detailed fee schedules are provided in your investment agreement and client documentation. 
                Fees may be deducted from your investment returns or charged separately.
              </p>
            </section>

            {/* Prohibited Activities */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">6. Prohibited Activities</h2>
              <p className="text-gray-600 mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Use our services for illegal activities or money laundering</li>
                <li>Provide false or misleading information</li>
                <li>Attempt to circumvent our security measures</li>
                <li>Share your account credentials with unauthorized parties</li>
                <li>Engage in market manipulation or insider trading</li>
                <li>Use our services to finance terrorism or sanctioned activities</li>
                <li>Interfere with our systems or other users' accounts</li>
              </ul>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">7. Limitation of Liability</h2>
              <div className="bg-gray-50 p-6 rounded-lg mb-4">
                <p className="text-gray-700 text-sm mb-4">
                  <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong>
                </p>
                <ul className="list-disc pl-6 text-gray-700 text-sm space-y-2">
                  <li>We exclude all warranties, whether express or implied</li>
                  <li>Our liability is limited to the fees paid by you in the 12 months preceding the claim</li>
                  <li>We are not liable for indirect, consequential, or punitive damages</li>
                  <li>We do not guarantee investment performance or returns</li>
                  <li>Market losses are not our responsibility</li>
                </ul>
              </div>
              <p className="text-gray-600 text-sm">
                This limitation does not apply to liability for death or personal injury caused by our negligence, 
                fraud, or other matters where liability cannot be excluded by law.
              </p>
            </section>

            {/* Regulatory Compliance */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4 flex items-center">
                <Shield className="h-6 w-6 text-gold-600 mr-2" />
                8. Regulatory Compliance
              </h2>
              <p className="text-gray-600 mb-4">
                HULA CAPITAL LIMITED operates in accordance with applicable UK and international regulations, including:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Financial Conduct Authority (FCA) rules and guidance</li>
                <li>Money Laundering, Terrorist Financing and Transfer of Funds Regulations</li>
                <li>Market Abuse Regulation (MAR)</li>
                <li>General Data Protection Regulation (GDPR)</li>
                <li>Consumer Credit Act where applicable</li>
              </ul>
              <p className="text-gray-600">
                You agree to cooperate with all regulatory requirements and provide information as requested 
                for compliance purposes.
              </p>
            </section>

            {/* Termination */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">9. Termination</h2>
              
              <h3 className="text-xl font-semibold text-navy-800 mb-3">Your Right to Terminate</h3>
              <p className="text-gray-600 mb-4">
                You may terminate your relationship with us at any time by providing written notice. 
                Termination may be subject to settlement of outstanding obligations and disposal of investments.
              </p>

              <h3 className="text-xl font-semibold text-navy-800 mb-3">Our Right to Terminate</h3>
              <p className="text-gray-600 mb-4">We may terminate or suspend your account if:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>You breach these Terms or applicable laws</li>
                <li>You provide false or misleading information</li>
                <li>Your account remains inactive for an extended period</li>
                <li>We are required to do so by law or regulation</li>
                <li>We decide to discontinue services</li>
              </ul>
            </section>

            {/* Dispute Resolution */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">10. Dispute Resolution</h2>
              
              <h3 className="text-xl font-semibold text-navy-800 mb-3">Complaints Procedure</h3>
              <p className="text-gray-600 mb-4">
                If you have a complaint, please contact us first to try to resolve the matter. 
                We will investigate and respond to complaints promptly and fairly.
              </p>

              <h3 className="text-xl font-semibold text-navy-800 mb-3">Financial Ombudsman Service</h3>
              <p className="text-gray-600 mb-4">
                If you are not satisfied with our response, you may be entitled to refer your complaint 
                to the Financial Ombudsman Service (financial-ombudsman.org.uk).
              </p>

              <h3 className="text-xl font-semibold text-navy-800 mb-3">Governing Law</h3>
              <p className="text-gray-600 mb-4">
                These Terms are governed by English law, and disputes will be subject to the exclusive 
                jurisdiction of the English courts.
              </p>
            </section>

            {/* Changes to Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">11. Changes to These Terms</h2>
              <p className="text-gray-600 mb-4">
                We may update these Terms from time to time to reflect changes in our services, 
                legal requirements, or business practices. We will notify you of material changes 
                by posting the updated Terms on our website and indicating the last updated date.
              </p>
              <p className="text-gray-600">
                Your continued use of our services after changes take effect constitutes acceptance 
                of the updated Terms.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">12. Contact Information</h2>
              <p className="text-gray-600 mb-4">
                For questions about these Terms or our services, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-navy-900 mb-2">HULA CAPITAL LIMITED</h4>
                    <p className="text-gray-600 text-sm">
                      10th Floor 19 Sun St<br />
                      London, EC2A 2FJ<br />
                      United Kingdom
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm mb-2">
                      <strong>Email:</strong> info@hulacapital.com
                    </p>
                    <p className="text-gray-600 text-sm mb-2">
                      <strong>Phone:</strong> +44 20 7946 0958
                    </p>
                    <p className="text-gray-600 text-sm">
                      <strong>Company No:</strong> 16534250
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Effective Date */}
            <section className="border-t pt-6">
              <p className="text-gray-500 text-sm">
                These Terms of Service are effective as of December 2024 and supersede all prior agreements 
                between you and HULA CAPITAL LIMITED relating to your use of our services.
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage; 